import {
  Lead,
  GuideSection,
  P,
  UL,
  OL,
  LI,
  Term,
  GuideLink,
  Callout,
  GuideTable,
} from './guide-ui';

/**
 * Body for "What is SIEM?", the pillar/anchor of the Security cluster.
 * Links out to every related guide (RBA, UEBA, SOAR, detection-as-code,
 * agentic SOC, compliance) to build internal topical authority.
 */
export function WhatIsSiemGuideBody() {
  return (
    <>
      <Lead>
        SIEM, Security Information and Event Management, is the centralised system
        a security team uses to collect logs from across its environment, detect
        threats by correlating events, and investigate and respond to incidents.
        It is the cornerstone of most security operations and a practical
        requirement for frameworks like NIS2 and DORA. This guide explains what a
        SIEM is, what it does, and how the modern, AI-era SIEM has evolved.
      </Lead>

      <GuideSection id="what-is-siem" title="What is SIEM?">
        <P>
          <Term>Security Information and Event Management (SIEM)</Term> is a category
          of security software that combines two earlier technologies,{' '}
          <Term>Security Information Management (SIM)</Term> and{' '}
          <Term>Security Event Management (SEM)</Term>, to enable real-time analysis
          of security events alongside long-term log storage and reporting. The term
          was coined by Gartner analysts in 2005, and SIEM has been the backbone of
          security operations ever since.
        </P>
        <P>
          In plain terms: a SIEM centralises the logs and security telemetry from
          your servers, applications, network and cloud, looks across them for signs
          of an attack, raises alerts on what matters, and keeps the data so you can
          investigate and prove compliance.
        </P>
      </GuideSection>

      <GuideSection id="sim-vs-sem" title="SIM vs. SEM: the two halves">
        <GuideTable
          head={['', 'SIM (Security Information Management)', 'SEM (Security Event Management)']}
          rows={[
            ['Focus', 'Long-term storage, analysis and reporting of log data', 'Real-time monitoring and alerting'],
            ['Use case', 'Compliance, forensics, the archive', 'Live threat detection and response'],
            ['Time horizon', 'Historical', 'Now'],
          ]}
        />
        <P>
          A SIEM is the union of the two: the live detection of SEM plus the
          retention and reporting of SIM, on one platform.
        </P>
      </GuideSection>

      <GuideSection id="what-it-does" title="What does a SIEM do?">
        <UL>
          <LI><Term>Log collection &amp; aggregation</Term>: ingest logs and events from across the estate into one place.</LI>
          <LI><Term>Normalisation</Term>: parse different log formats into a consistent structure that can be searched and compared.</LI>
          <LI><Term>Correlation &amp; detection</Term>: apply detection rules and correlate events across sources to surface attacks that no single log shows.</LI>
          <LI><Term>Alerting</Term>: notify analysts of suspicious activity, ideally prioritised by risk rather than volume.</LI>
          <LI><Term>Investigation (TDIR)</Term>: give analysts fast search and context to triage, investigate, and respond, that is, threat detection, investigation and response.</LI>
          <LI><Term>Dashboards &amp; reporting</Term>: visualise security posture and produce compliance evidence.</LI>
          <LI><Term>Retention</Term>: store logs long enough for forensics and regulatory requirements.</LI>
        </UL>
      </GuideSection>

      <GuideSection id="how-it-works" title="How a SIEM works">
        <OL>
          <LI><Term>Collect</Term>: agents and integrations forward logs and telemetry from endpoints, servers, network, identity, and cloud.</LI>
          <LI><Term>Normalise &amp; enrich</Term>: events are parsed into a common schema and enriched (geo-IP, threat intel, asset context).</LI>
          <LI><Term>Correlate &amp; detect</Term>: detection rules and analytics evaluate the stream, including multi-step sequences across sources.</LI>
          <LI><Term>Prioritise &amp; alert</Term>: matches are scored and the highest-risk cases are raised for analysts.</LI>
          <LI><Term>Investigate &amp; respond</Term>: analysts (increasingly with AI agents) work the case and trigger response.</LI>
        </OL>
      </GuideSection>

      <GuideSection id="modern-siem" title="The modern SIEM: what changed">
        <P>
          A 2026 SIEM is far more than rules over logs. The category has absorbed
          several capabilities, each of which has its own guide:
        </P>
        <UL>
          <LI>
            <GuideLink href="/guides/what-is-risk-based-alerting">Risk-based alerting (RBA)</GuideLink>:{' '}
            attribute risk to entities instead of firing an alert per rule, to cut
            alert fatigue.
          </LI>
          <LI>
            <GuideLink href="/guides/what-is-ueba">UEBA</GuideLink>: behavioral
            analytics that baseline users and entities and flag anomalies.
          </LI>
          <LI>
            <GuideLink href="/guides/what-is-soar">SOAR</GuideLink>: orchestration,
            automation and response to act on detections.
          </LI>
          <LI>
            <GuideLink href="/guides/detection-as-code">Detection-as-code</GuideLink>:{' '}
            manage detections like software, with version control and testing.
          </LI>
          <LI>
            <Term>Threat intelligence</Term> and <Term>AI/ML</Term>: enrich and
            triage at machine speed, leading toward the{' '}
            <GuideLink href="/guides/what-is-an-agentic-soc">agentic SOC</GuideLink>.
          </LI>
        </UL>
      </GuideSection>

      <GuideSection id="challenges" title="Common SIEM challenges">
        <UL>
          <LI><Term>Cost</Term>: per-GB or per-host pricing makes ingesting more data punishingly expensive, so teams under-log.</LI>
          <LI><Term>Alert fatigue</Term>: rule-per-alert designs bury analysts; risk-based alerting is the modern answer.</LI>
          <LI><Term>Tuning burden</Term>: detections need constant maintenance to stay useful.</LI>
          <LI><Term>Data silos</Term>: separate security, observability and search stacks fragment the picture and the budget.</LI>
        </UL>
      </GuideSection>

      <GuideSection id="compliance" title="SIEM and compliance">
        <P>
          Frameworks rarely name &quot;SIEM&quot; outright, but their requirements, such as
          continuous monitoring, correlation, alerting, retention, and incident
          reporting, are hard to meet without one. See the{' '}
          <GuideLink href="/guides/nis2-logging-requirements">NIS2</GuideLink> and{' '}
          <GuideLink href="/guides/dora-logging-monitoring-requirements">DORA</GuideLink>{' '}
          logging guides for how the EU regimes translate into SIEM-shaped
          obligations, and{' '}
          <GuideLink href="/guides/log-retention-requirements">log retention requirements</GuideLink>{' '}
          for how long to keep the data.
        </P>
      </GuideSection>

      <GuideSection id="how-logpulse-helps" title="How LogPulse approaches SIEM">
        <P>
          LogPulse runs a full SIEM on the <Term>same LPQL + ClickHouse engine</Term>{' '}
          as log search and service intelligence, with no separate, separately-priced
          security data store. It combines MITRE-tagged detections,{' '}
          <GuideLink href="/guides/what-is-risk-based-alerting">risk-based alerting</GuideLink>{' '}
          (one bounded 0–100 score per entity), <GuideLink href="/guides/what-is-ueba">UEBA</GuideLink>,
          threat intelligence, and response actions behind a human-approval gate,
          an <GuideLink href="/guides/what-is-an-agentic-soc">agentic SOC</GuideLink>{' '}
          with a human in the loop. Pricing is flat, and data stays EU-hosted and
          GDPR-compliant.
        </P>
        <Callout title="One engine, not a stack">
          <p>
            Because search, SIEM and service intelligence share one engine, you do
            not feed and pay for a separate security data lake. See{' '}
            <GuideLink href="/siem">Security Monitoring (SIEM)</GuideLink> for the
            full capability.
          </p>
        </Callout>
      </GuideSection>
    </>
  );
}
